import * as React from "react";
import styled from "styled-components";

import { TextInput } from "--floponents-web/atoms/TextInput";
import { RadioButton } from "--floponents-web/atoms/RadioButton";
import { SelectInput, tSelectValue } from "--floponents-web/atoms/SelectInput";
import { DayPicker } from "./components/DayPicker";
import { DatePickerInput } from "./DatePicker";
import { RepeatSchedule, RepeatUntil } from "--root/static_types/shared.types"
import { Strings } from '--root/strings/en';
import { Errors } from '--root/Errors';

const STRINGS = Strings.dateTime

type RepeatOptionsProps = {
    date?: Date
    onChange: (schedule: RepeatSchedule) => void,
    errorMessage?: Errors.FormFieldError
}

const getWeekInMonth = (date: Date) => {
    const day = date.getDay();
    const firstDay = new Date(date);
    firstDay.setDate(1);
    firstDay.setDate(((7 + day - firstDay.getDay()) % 7) + 1);
    const week = Math.ceil(date.getDate() / 7);
    return week;
}

const getClampedDate = (date: Date) => {
    return Math.min(date.getDate(), 28);
}

const getClampedLeapDate = (date: Date) => {
    return date.getMonth() == 1 ? Math.max(28, date.getDate()) : date.getDate()
}

const getRepeatOptions = (date?: Date) => {
    if (!date) return [];

    const day = date.getDay();
    const week = getWeekInMonth(date);
    const strWeek = STRINGS.weeks[week - 1];
    const strDay = STRINGS.days[day];

    const clampedDate = getClampedDate(date);
    const ordinalSuffixes = ["th", "st", "nd", "rd"];
    const ordinalSuffix = ordinalSuffixes[(clampedDate % 100 - 20) % 10] || ordinalSuffixes[clampedDate % 100] || ordinalSuffixes[0]

    const clampedLeapDate = getClampedLeapDate(date);
    const strMonth = STRINGS.months[date.getMonth()];

    return [
        { key: "none", value: "No repeat" },
        { key: "daily", value: "Repeat Daily" },
        { key: "weekly", value: "Repeat Weekly" },
        { key: "monthlyDay", value: `Repeat Monthly on the ${strWeek} ${strDay}` },
        { key: "monthlyDate", value: `Repeat Monthly on the ${clampedDate}${ordinalSuffix}` },
        { key: "annually", value: `Repeat Annually on the ${clampedLeapDate} of ${strMonth}` }
    ]
}

type tRepeatType = "forever" | "for" | "until";

const RepeatOptions: React.FC<RepeatOptionsProps> = (props) => {

    const { date, onChange, errorMessage } = props;

    const repeatOptions = React.useMemo(() => getRepeatOptions(date), [date]);

    const [repeatType, setRepeatType] = React.useState<tSelectValue<string>>();
    const [repeatDays, setRepeatDays] = React.useState<[boolean, boolean, boolean, boolean, boolean, boolean, boolean]>([false, false, false, false, false, false, false]);
    const [repeatEnd, setRepeatEnd] = React.useState<tRepeatType>("forever");
    const [repeatUntil, setRepeatUntil] = React.useState<Date>();
    const [repeatFor, setRepeatFor] = React.useState<number>(1);

    const repeatPlaceholder = date ? STRINGS.repeatSchedule : STRINGS.selectFirst;

    React.useEffect(() => {
        // Report back the changes to parent...
        if (!repeatType || !date) return;

        let until: RepeatUntil;
        switch (repeatEnd) {
            case "for":
                until = { format: "count", value: repeatFor ?? Number.MAX_SAFE_INTEGER };
                break;
            case "until":
                until = { format: "date", value: repeatUntil ? repeatUntil.getTime() : NaN };
                break
            case "forever":
                until = { format: "forever" }
                break
        }

        switch (repeatType.key) {
            case "none":
                onChange({ type: "none", nextDate: date.getTime() })
                break;
            case "daily":
                onChange({ type: "daily", until: until, nextDate: date.getTime() })
                break;
            case "weekly":
                onChange({ type: "weekly", weekDay: repeatDays, until: until, nextDate: date.getTime() });
                break;
            case "monthlyDay":
                onChange({ type: "monthlyDay", weekDay: date?.getDay(), week: getWeekInMonth(date), until: until, nextDate: date.getTime() });
                break;
            case "monthlyDate":
                onChange({ type: "monthlyDate", date: getClampedDate(date), until: until, nextDate: date.getTime() });
                break;
            case "annually":
                onChange({ type: "annually", month: date.getMonth(), date: getClampedLeapDate(date), until: until, nextDate: date.getTime() });
                break;
        }
    }, [repeatType, repeatDays, repeatEnd, repeatUntil, repeatFor, date]);

    return <>
        <SelectInput
            size="small"
            label="Repeat"
            placeholder={repeatPlaceholder}
            value={repeatType}
            options={repeatOptions}
            onChange={setRepeatType}
            message={errorMessage}
        />

        {!!(repeatType && repeatType.key == "weekly") && <>
            <DayPicker onChange={setRepeatDays} />
        </>}

        {!!(repeatType && repeatType.key != "none") && <div>
            <RepeatRadioGroup>
                <RadioButton
                    value="forever"
                    checked={repeatEnd == "forever"}
                    size="small"
                    onChange={(e) => setRepeatEnd(e.target.value as any)} />
                <span>{STRINGS.repeatForever}</span>
            </RepeatRadioGroup>

            <RepeatRadioGroup>
                <RadioButton
                    value="for"
                    checked={repeatEnd == "for"}
                    size="small"
                    onChange={(e) => setRepeatEnd(e.target.value as any)} />
                {STRINGS.repeatFor(<TextInput size="small" type="number" value={repeatFor} onChange={(e, v) => setRepeatFor(parseInt(v, 10))} min={1} step={1} />)}
            </RepeatRadioGroup>

            <RepeatRadioGroup>
                <RadioButton
                    value="until"
                    checked={repeatEnd == "until"}
                    size="small"
                    onChange={(e) => setRepeatEnd(e.target.value as any)} />
                {STRINGS.repeatUntil(<DatePickerInput date={repeatUntil} size="small" onPick={setRepeatUntil} />)}
            </RepeatRadioGroup>
        </div>}
    </>

}

const RepeatRadioGroup = styled.div`
    display: flex;
    align-items: center;
    height: 32px;

    margin: ${props => props.theme.spacing(0, 0, 1, 0)};

    >span {
        ${props => props.theme.typography.body2}
        color: ${props => props.theme.palette.text.primary};
        margin: 0;
    }

    >*:not(:last-child) { margin-right: 0.3em; }
    >*:nth-child(1) { margin-right: ${props => props.theme.spacing(1)}; }
    :last-child { margin: 0; }
`;

export default RepeatOptions;
export { RepeatOptions, RepeatOptionsProps };